import React from "react";
import StaticContainer from "../layouts/StaticContainer";
import styles from "../styles/pages/contactus.module.scss";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import GeneralForm from "../components/_shared/GeneralForm";
import { hubspot } from "../constants";
import LocalLibraryRoundedIcon from "@mui/icons-material/LocalLibraryRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import SupervisedUserCircleRoundedIcon from "@mui/icons-material/SupervisedUserCircleRounded";
import PermMediaRoundedIcon from "@mui/icons-material/PermMediaRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import { SocialIconLinks } from "../components/press/SocialMedia";
import Link from "next/link";
import { useTranslation } from "next-i18next";
import { cmsPageProps } from "../lib/pageProps";
import Meta from "../layouts/Meta";
import { contactFacts } from "../constants/company";
import { useBrandContact } from "../lib/LiveContext";

const ContactUs = () => {
  const { t } = useTranslation("contact-us");
  const contact = contactFacts(useBrandContact());

  const contactCard = [
    {
      contactHeading: t("help-centre-h3"),
      contactDescription: t("help-centre-p"),
      contactIcon: (
        <LocalLibraryRoundedIcon
          className={styles.contactImage}
          style={{ color: "#DB4B4B" }}
        />
      ),
      cardColor: "#DB4B4B",
      cardURL: "/help-centre",
    },
    {
      contactHeading: t("careers-h3"),
      contactDescription: t("careers-p"),
      contactIcon: (
        <BusinessCenterRoundedIcon
          className={styles.contactImage}
          style={{ color: "#179F84" }}
        />
      ),
      cardColor: "#179F84",
      cardURL: "/careers",
    },
    {
      contactHeading: t("partnership-h3"),
      contactDescription: t("partnership-p"),
      contactIcon: (
        <SupervisedUserCircleRoundedIcon
          className={styles.contactImage}
          style={{ color: "#f8be15" }}
        />
      ),
      cardColor: "#F8BE15",
      cardURL: "/partners",
    },
    {
      contactHeading: t("media-inq-h3"),
      contactDescription: t("media-inq-p"),
      contactIcon: (
        <PermMediaRoundedIcon
          className={styles.contactImage}
          style={{ color: "#2481BC" }}
        />
      ),
      cardColor: "#2481BC",
      cardURL: "/press",
    },
  ];

  // The three ways to reach the Foundation, in the SOLS Group secondary palette
  // the other group sites' cards use (earth red, money green, unity blue): the
  // circle takes the colour, the label a shade of it that passes as text. The icon is decorative: the label says what the card does.
  const reachCards = [
    {
      heading: t("call-h3"),
      value: contact.phoneDisplay,
      href: `tel:${contact.phone}`,
      colour: "#CC5803",
      textColour: "#B54E03",
      icon: <PhoneRoundedIcon aria-hidden="true" />,
    },
    {
      heading: t("whatsapp-h3"),
      value: contact.whatsappUrl.replace(/^https:\/\//, ""),
      href: contact.whatsappUrl,
      external: true,
      colour: "#428A59",
      textColour: "#3B7C50",
      icon: <WhatsAppIcon aria-hidden="true" />,
    },
    {
      heading: t("email-h3"),
      value: contact.email,
      href: `mailto:${contact.email}`,
      colour: "#27619C",
      textColour: "#27619C",
      icon: <EmailRoundedIcon aria-hidden="true" />,
    },
  ];

  return (
    <StaticContainer title={t("title")} category="About">
      <Meta
        title={t("title")}
        description={t("meta-description")}
        keywords={t("meta-keyword")}
      />

      <section className="centerContent">
        <h2>{t("how-help-h2")}</h2>

        <Grid container spacing={4} className={styles.contactList}>
          {contactCard?.map((link) => (
            <Grid
              item
              xs={12}
              sm={3}
              key={"contactpost-" + link.contactHeading}
            >
              <Link href={link.cardURL}>
                <Card
                  elevation={0}
                  className={styles.contactCard}
                  style={{ borderColor: link.cardColor }}
                >
                  {link.contactIcon}

                  <div className={styles.contactContent}>
                    <h3>{link.contactHeading}</h3>
                    <p> {link.contactDescription}</p>
                  </div>
                </Card>
              </Link>
            </Grid>
          ))}
        </Grid>
      </section>

      {/* Where the Foundation is, beside the form: two fifths and three
          fifths, as on the other group sites, stacking below md. A CSS grid
          rather than MUI's Grid, whose twelve columns cannot make fifths. */}
      <section className={styles.formSection}>
        <h2>{t("get-in-touch-h2")}</h2>
        <div className={styles.formGrid}>
          <div>
            <div className={styles.mapCard}>
              <iframe
                src={MAPS_EMBED_SRC}
                title={t("ourhq-h2")}
                height="300"
                loading="lazy"
              ></iframe>
              <div className={styles.mapBody}>
                <div>
                  <h2>{contact.legalName}</h2>
                  <span className={styles.registration}>
                    {contact.registrationDisplay}
                  </span>
                </div>
                <dl>
                  <div>
                    <dt>{t("address-label")}</dt>
                    <dd>
                      {contact.addressLines.map((line, i) => (
                        <React.Fragment key={line}>
                          {i > 0 && <br />}
                          {line}
                        </React.Fragment>
                      ))}
                    </dd>
                  </div>
                  <div>
                    <dt>{t("hours-label")}</dt>
                    <dd>{t("hours-value")}</dd>
                  </div>
                </dl>
                {/* Equal halves, so neither app reads as the default. Each in
                    its own app's colour, with its logo. A lone button takes
                    the whole row. */}
                {(contact.googleMapsUrl || contact.wazeUrl) && (
                  <div
                    className={
                      contact.navigationLone
                        ? `${styles.navButtons} ${styles.navButtonsLone}`
                        : styles.navButtons
                    }
                  >
                    {contact.googleMapsUrl && (
                      <a
                        href={contact.googleMapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${styles.navButton} ${styles.googleButton}`}
                      >
                        <GoogleMapsLogo />
                        Google Maps
                      </a>
                    )}
                    {contact.wazeUrl && (
                      <a
                        href={contact.wazeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${styles.navButton} ${styles.wazeButton}`}
                      >
                        <WazeLogo />
                        Waze
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <GeneralForm
              title={t("form-h3")}
              description={t("form-p")}
              portalId={hubspot.PORTAL_ID}
              formId={hubspot.CONTACT_US_ID}
              className={styles.formCard}
            />
          </div>
        </div>
      </section>

      {/* The ways to reach the Foundation, one card each, then the profiles
          to follow. WhatsApp is a card here rather than a social icon: it is a
          way to reach the Foundation, not a profile to follow. */}
      <section className="fullWidthBG last">
        <div className="fullWidthInner centerContent">
          <h2>{t("more-ways-h2")}</h2>
          <Grid container spacing={4} className={styles.reachList}>
            {reachCards.map((card) => (
              <Grid item xs={12} sm={4} key={"reach-" + card.heading}>
                <a
                  href={card.href}
                  className={styles.reachCard}
                  {...(card.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                >
                  <span
                    className={styles.reachCircle}
                    style={{ backgroundColor: card.colour }}
                  >
                    {card.icon}
                  </span>
                  <h3
                    className={styles.reachLabel}
                    style={{ color: card.textColour }}
                  >
                    {card.heading}
                  </h3>
                  <span className={styles.reachValue}>{card.value}</span>
                </a>
              </Grid>
            ))}
          </Grid>

          <div className={styles.followUs}>
            <h3>{t("follow-h3")}</h3>
            <SocialIconLinks />
          </div>
        </div>
      </section>
    </StaticContainer>
  );
};

/**
 * The SOLS Foundation Google Business Profile, from the profile's own Share >
 * Embed a map code. It targets the Place by its ID, so the embed shows the live
 * listing card and follows any change made on the profile with no edit here.
 */
const MAPS_EMBED_SRC =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7968.197747629925!2d101.70418869427677!3d3.068244513498661!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc4a8227225fe7%3A0x9f259f7d3bba623a!2sSOLS%2024%2F7%20(Science%20of%20Life%20Studies%2024%2F7)!5e0!3m2!1sen!2smy!4v1585256018928!5m2!1sen!2smy";

/** The Google Maps pin, in Google's own colours. */
function GoogleMapsLogo() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 92.3 132.3"
      aria-hidden="true"
    >
      <path
        fill="#1a73e8"
        d="M60.2 2.2C55.8.8 51 0 46.1 0 32 0 19.3 6.4 10.8 16.5l21.8 18.3z"
      />
      <path
        fill="#ea4335"
        d="M10.8 16.5C4.1 24.5 0 34.9 0 46.1c0 8.7 1.7 15.7 4.6 22l28-33.3z"
      />
      <path
        fill="#4285f4"
        d="M46.2 28.5c9.8 0 17.7 7.9 17.7 17.7 0 4.3-1.6 8.3-4.2 11.4 0 0 13.9-16.6 27.5-32.7-5.6-10.8-15.3-19-27-22.7L32.6 34.8c3.3-3.8 8.1-6.3 13.6-6.3"
      />
      <path
        fill="#fbbc04"
        d="M46.2 63.8c-9.8 0-17.7-7.9-17.7-17.7 0-4.3 1.5-8.3 4.1-11.3l-28 33.3c4.8 10.6 12.8 19.2 21 29.9l34.1-40.5c-3.3 3.9-8.1 6.3-13.5 6.3"
      />
      <path
        fill="#34a853"
        d="M59.1 109.2c15.4-24.1 33.3-35 33.3-63 0-7.7-1.9-14.9-5.2-21.3L25.6 98c2.6 3.4 5.3 7.3 7.9 11.3 9.4 14.5 6.8 23.1 12.8 23.1s3.4-8.7 12.8-23.2"
      />
    </svg>
  );
}

/** The Waze face, white with its dark outline, for the Waze-blue button. */
function WazeLogo() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 108 100"
      aria-hidden="true"
    >
      <path
        fill="#fff"
        d="M58.9 83.8H49c-1.1-5.5-6-9.7-11.8-9.7-4.3 0-8 2.2-10.2 5.5v.1c-3.6-1.8-6.9-4.3-9.7-7.2-3.4-3.4-5.3-6.5-6.1-8.5 2.2-.5 4.2-1.7 5.8-3.4 2.1-2.2 3.2-5.2 3.2-8.2v-7.2c0-8.5 2.8-16.9 8.1-23.6 7.5-9.6 18.6-15 30.6-15 10.3 0 20 4 27.3 11.3s11.3 17 11.3 27.3-4 20-11.3 27.3a38.84 38.84 0 0 1-27.3 11.3"
      />
      <path d="M102.3 45.1c0-11.6-4.5-22.5-12.7-30.7A43.88 43.88 0 0 0 58.9 1.7c-13.3 0-25.7 6-34.2 16.7-6.1 7.7-9.3 17.2-9.3 27v7c0 3.6-2.5 7-7.5 7.3-1.2.1-2.2.9-2.2 2.1-.2 3.3 3.3 9.3 8.1 14.1 3.3 3.4 7.2 6.1 11.4 8.2a12.08 12.08 0 0 0 11.9 14.2c5.9 0 10.7-4.1 11.8-9.6H59c1.3 6.8 8.4 11.5 16 8.9 6.6-2.2 9.5-9.7 7.1-15.8 2.6-1.7 5.1-3.7 7.4-5.9a43.2 43.2 0 0 0 12.8-30.8M58.9 83.8H49c-1.1-5.5-6-9.7-11.8-9.7-4.3 0-8 2.2-10.2 5.5v.1c-3.6-1.8-6.9-4.3-9.7-7.2-3.4-3.4-5.3-6.5-6.1-8.5 2.2-.5 4.2-1.7 5.8-3.4 2.1-2.2 3.2-5.2 3.2-8.2v-7.2c0-8.5 2.8-16.9 8.1-23.6 7.5-9.6 18.6-15 30.6-15 10.3 0 20 4 27.3 11.3s11.3 17 11.3 27.3-4 20-11.3 27.3a38.84 38.84 0 0 1-27.3 11.3" />
      <circle cx="78.2" cy="35.5" r="4.8" />
      <circle cx="49.2" cy="35.5" r="4.8" />
      <path d="M50.7 51.3c-.4-.8-1.3-1.4-2.2-1.4a2.4 2.4 0 0 0-2.2 3.4c3.1 6.5 9.7 11.1 17.5 11.1s14.4-4.5 17.5-11.1c.7-1.6-.4-3.4-2.2-3.4H79c-.9 0-1.7.5-2.1 1.4-2.3 4.9-7.3 8.3-13.1 8.3S53 56.2 50.7 51.3" />
    </svg>
  );
}

export const getStaticProps = async ({ locale }) => ({
  props: {
    ...(await cmsPageProps(locale, ["contact-us"])),
  },
  revalidate: 60,
});

export default ContactUs;
